import React from "react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

// Date
import apps from "./../../data/apps.json";

// Components
import Cursor from "./Cursor/Cursor";
import Navbar from "./Navbar/Navbar";
import DesktopIcons from "./DesktopIcons/DesktopIcons";
import Dock from "./Dock/Dock";
import WindowFrame from "./../Windows/WindowFrame";

// Windows
import WelcomeWindow from "../Windows/WelcomeWindow/WelcomeWindow";
import AboutMeWindow from "../Windows/AboutMeWindow/AboutMeWindow";
import WorkWindow from "../Windows/WorkWindow/WorkWindow";
import SideProjectWindow from "../Windows/SideProjectWindow/SideProjectWindow";
import LabWindow from "../Windows/LabWindow/LabWindow";
import GuestBookWindow from "../Windows/GuestBookWindow/GuestBookWindow";
import MusicWindow from "../Windows/MusicWindow/MusicWindow";
import BlogWindow from "../Windows/BlogWindow/BlogWindow";
import PrivacyPolicyWindow from "../Windows/PrivacyPolicyWindow/PrivacyPolicyWindow";
import ErrorWindow from "../Windows/ErrorWindow/ErrorWindow";
import Alert from "./Alert/Alert";

// SCSS
import "./Layout.scss";

function Layout({ language, setLanguage }) {
  // Cursor
  const [hoveredElement, setHoveredElement] = useState(null);

  // Window Frame (Open/Close, Active, Address)
  const [openWindows, setOpenWindows] = useState([]);
  const [activeWindow, setActiveWindow] = useState(
    window.innerWidth > 768 ? "welcome" : null,
  );
  const navigate = useNavigate();

  const openWindow = (id) => {
    if (!openWindows.includes(id)) {
      setOpenWindows([...openWindows, id]);
    }
    setActiveWindow(id);
    navigate(`/${id}`);
  };

  const closeWindow = (id) => {
    setOpenWindows(openWindows.filter((windowId) => windowId !== id));
  };

  // Window Render
  const renderAppContent = (id) => {
    switch (id) {
      case "welcome":
        return (
          <WelcomeWindow
            setHoveredElement={setHoveredElement}
            lang={language}
          />
        );
      case "about-me":
        return (
          <AboutMeWindow
            setHoveredElement={setHoveredElement}
            lang={language}
          />
        );
      case "work":
        return (
          <WorkWindow setHoveredElement={setHoveredElement} lang={language} />
        );
      case "side-project":
        return (
          <SideProjectWindow
            setHoveredElement={setHoveredElement}
            lang={language}
          />
        );
      case "lab":
        return (
          <LabWindow setHoveredElement={setHoveredElement} lang={language} />
        );
      case "guest-book":
        return (
          <GuestBookWindow
            setHoveredElement={setHoveredElement}
            lang={language}
          />
        );
      case "music":
        return (
          <MusicWindow setHoveredElement={setHoveredElement} lang={language} />
        );
      case "blog":
        return (
          <BlogWindow setHoveredElement={setHoveredElement} lang={language} />
        );
      case "privacy-policy":
        return <PrivacyPolicyWindow />;
      case "error":
        return (
          <ErrorWindow
            setHoveredElement={setHoveredElement}
            lang={language}
            onClose={() => closeWindow("error")}
          />
        );
      default:
        return <div>EMPTY</div>;
    }
  };

  const location = useLocation();

  useEffect(() => {
    const pathId = location.pathname.split("/")[1];

    if (pathId) {
      const isExist = apps.some((app) => app.id === pathId);

      if (isExist) {
        if (!openWindows.includes(pathId)) {
          setOpenWindows((prev) => [...prev, pathId]);
        }
        setActiveWindow(pathId);
      } else {
        if (!openWindows.includes("error")) {
          setOpenWindows((prev) => [...prev, "error"]);
        }
        setActiveWindow("error");
      }
    }
  }, [location]);

  // Ascii Art
  useEffect(() => {
    const asciiArt = `
　　∩_∩
　 ∧∧　 |
⊂(_ _⊂ノ　
    `;
    console.log(
      "%c" + asciiArt,
      "color: #ecb3d1; font-weight: bold; font-family: monospace;",
    );
    console.log(
      "%cContact: chae3929@gmail.com",
      "color: #8e8e8e; font-size: 14px;",
    );
  }, []);

  return (
    <div className="Desktop">
      <Navbar
        setHoveredElement={setHoveredElement}
        language={language}
        setLanguage={setLanguage}
      />

      <main className="Screen">
        <div
          className="Screen__Wallpaper"
          onClick={() => {
            setOpenWindows([]);
            setActiveWindow(null);
          }}
        ></div>

        <DesktopIcons
          setHoveredElement={setHoveredElement}
          openWindow={openWindow}
        />

        <div className="WindowLayer">
          <AnimatePresence>
            {openWindows.map((id) => {
              const appInfo = apps.find((app) => app.id === id);
              return (
                <WindowFrame
                  key={id}
                  onPointerDown={() => {
                    setActiveWindow(id);
                    if (location.pathname.startsWith(`/${id}`) === false) {
                      navigate(`/${id}`);
                    }
                  }}
                  style={{ zIndex: activeWindow === id ? 1000 : 10 }}
                  title={appInfo.name}
                  initialWidth={appInfo.width || 600}
                  initialHeight={appInfo.height || 600}
                  onClose={() => closeWindow(id)}
                  setHoveredElement={setHoveredElement}
                >
                  {renderAppContent(id)}
                </WindowFrame>
              );
            })}
          </AnimatePresence>
        </div>
      </main>

      <Dock
        setHoveredElement={setHoveredElement}
        openWindow={openWindow}
        openWindows={openWindows}
      />

      <Alert setHoveredElement={setHoveredElement} openWindow={openWindow} />

      <Cursor hoveredElement={hoveredElement} />
    </div>
  );
}

export default Layout;
